import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth, formatErr } from "../AuthContext";
import { useI18n } from "../i18n";
import { Logo, LangToggle, ThemeToggle, Footer } from "../components/Frumy";
import { ShieldCheck, MagnifyingGlass, ChartLineUp, ArrowRight } from "@phosphor-icons/react";

export default function Landing() {
  const { login, register } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setLoading(true);
    try {
      if (mode === "register") await register(form.email, form.password, form.name);
      else await login(form.email, form.password);
      nav("/upload");
    } catch (e2) {
      setErr(formatErr(e2.response?.data?.detail) || e2.message);
    } finally { setLoading(false); }
  };

  const features = [
    { Icon: MagnifyingGlass, key: "Rileva abbonamenti nascosti" },
    { Icon: ShieldCheck, key: "Privacy al primo posto" },
    { Icon: ChartLineUp, key: "Spesa sotto controllo" },
  ];

  return (
    <div className="min-h-screen relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10" aria-hidden>
        <div className="absolute -top-24 -right-24 h-96 w-96 rounded-full brand-gradient opacity-[0.14] blur-3xl" />
        <div className="absolute top-1/3 -left-24 h-80 w-80 rounded-full bg-[var(--frumy-green)] opacity-[0.10] blur-3xl" />
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
        <Logo onClick={() => nav("/")} />
        <div className="flex items-center gap-3">
          <button data-testid="landing-privacy-link" onClick={() => nav("/privacy")}
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--text-muted)] hover:text-[var(--frumy-green)] transition-colors">
            <ShieldCheck size={16} aria-hidden />{t("privacyLink")}
          </button>
          <ThemeToggle />
          <LangToggle />
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 grid lg:grid-cols-2 gap-12 lg:gap-16 items-center pt-8 lg:pt-16 pb-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5 }}>
          <span className="text-xs tracking-[0.2em] uppercase font-bold text-[var(--frumy-green)]">
            {t("oneTime")} · Freemium
          </span>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight mt-4 leading-[1.05]">
            {t("tagline")}
          </h1>
          <p className="text-base leading-relaxed text-[var(--text-muted)] mt-5 max-w-md">{t("subtitle")}</p>
          <div className="flex flex-col gap-3 mt-8 max-w-sm">
            {features.map(({ Icon, key }, i) => (
              <div key={i} className="flex items-center gap-3">
                <span className="grid place-items-center h-9 w-9 rounded-lg brand-gradient text-white">
                  <Icon weight="duotone" size={20} aria-hidden />
                </span>
                <span className="font-semibold text-sm text-[var(--heading)]">{key}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: .5, delay: .15 }}
          className="frumy-card p-6 sm:p-8 max-w-md w-full lg:justify-self-end">
          <div className="flex gap-1 p-1 bg-[var(--frumy-muted)] rounded-full mb-6">
            {["register", "login"].map((m) => (
              <button key={m} data-testid={`tab-${m}`} onClick={() => { setMode(m); setErr(""); }}
                className={`flex-1 pill-btn py-2 text-sm font-bold ${mode === m ? "bg-white shadow-sm text-[var(--heading)]" : "text-[var(--text-muted)]"}`}>
                {m === "register" ? t("register") : t("login")}
              </button>
            ))}
          </div>
          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div>
                <label className="text-xs tracking-[0.15em] uppercase font-bold text-[var(--text-muted)]">{t("name")}</label>
                <input data-testid="name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1.5 w-full rounded-lg border border-[var(--frumy-border)] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-to)]" />
              </div>
            )}
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[var(--text-muted)]">{t("email")}</label>
              <input data-testid="email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-[var(--frumy-border)] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-to)]" />
            </div>
            <div>
              <label className="text-xs tracking-[0.15em] uppercase font-bold text-[var(--text-muted)]">{t("password")}</label>
              <input data-testid="password-input" type="password" required minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1.5 w-full rounded-lg border border-[var(--frumy-border)] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[var(--brand-to)]" />
            </div>
            {err && <p data-testid="auth-error" className="text-sm text-red-600 font-medium">{err}</p>}
            <button data-testid="auth-submit" type="submit" disabled={loading}
              className="pill-btn cta-metal w-full flex items-center justify-center gap-2 text-white font-bold py-3 disabled:opacity-60">
              {loading ? "..." : (mode === "register" ? t("getStarted") : t("login"))}
              {!loading && <ArrowRight weight="bold" size={18} aria-hidden />}
            </button>
          </form>
          <p className="text-center text-sm text-[var(--text-muted)] mt-5">
            {mode === "register" ? t("haveAccount") : t("noAccount")}{" "}
            <button onClick={() => setMode(mode === "register" ? "login" : "register")}
              className="font-bold text-gradient hover:opacity-80 transition-opacity">
              {mode === "register" ? t("login") : t("register")}
            </button>
          </p>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
}
